import { useCallback, useEffect, useState } from 'react';
import { getLocalCelloToken } from '@/utils/helper';
import { useFeatureFlag } from './useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { USER_TYPES } from 'src/enums/EUser';
import posthog from 'posthog-js';

export const useCello = () => {
	const isCelloEnabled = useFeatureFlag(FeatureFlag.CELLO_REFERRAL);

	const loggedInUser = useAppSelector(getLoggedInUser);

	const [attributionData, setAttributionData] = useState<{
		ucc?: string;
		referrerName?: string;
		campaignConfig?: any;
	}>({});

	const [isCelloInitialized, setIsCelloInitialized] = useState(false);
	const [isCelloHidden, setIsCelloHidden] = useState(false);

	const isCelloAvailable = () => {
		return isCelloEnabled && loggedInUser?.userType === USER_TYPES.CLIENT && window?.Cello;
	};

	const init = useCallback(
		(userData: any) => {
			if (!isCelloAvailable() || userData?.userType !== USER_TYPES.CLIENT) {
				return;
			}

			const celloToken = getLocalCelloToken();

			if (!celloToken || isCelloInitialized) {
				return;
			}

			window.cello = window?.cello || { cmd: [] };

			window?.cello?.cmd.push(async (cello) => {
				try {
					await cello.boot({
						productId: import.meta.env.VITE_APP_CELLO_PRODUCT_ID,
						token: celloToken,
						language: 'en',
						productUserDetails: {
							fullName: userData.businessName,
							firstName: userData.businessName,
							email: loggedInUser?.emailAddress,
						},
					});

					setIsCelloInitialized(true);
					setIsCelloHidden(false);
				} catch {
					setIsCelloHidden(true);
					console.error('Failed to init referral code');
				}
			});
		},
		[isCelloInitialized, isCelloEnabled, loggedInUser?.userType],
	);

	useEffect(() => {
		if (!isCelloInitialized) {
			setIsCelloHidden(true);
		}
	}, [isCelloInitialized]);

	const show = useCallback(() => {
		if (!isCelloAvailable()) {
			return;
		}
		window?.Cello('show');
	}, [isCelloEnabled, loggedInUser?.userType]);

	const open = useCallback(() => {
		if (!isCelloAvailable()) {
			return;
		}

		window?.Cello('open');

		posthog?.capture('cello-referral-open');
	}, [isCelloEnabled, loggedInUser?.userType]);

	const getAttributionData = useCallback(async () => {
		if (!isCelloAvailable() || !window?.CelloAttribution) {
			return {};
		}

		try {
			const [ucc, referrerName, campaignConfig] = await Promise.all([
				window?.CelloAttribution('getUcc'),
				window?.CelloAttribution('getReferrerName'),
				window?.CelloAttribution('getCampaignConfig'),
			]);

			const data = {
				ucc: ucc || undefined,
				referrerName: referrerName || undefined,
				campaignConfig: campaignConfig || undefined,
			};

			setAttributionData(data);
			return data;
		} catch (error) {
			console.error('Failed to get attribution data:', error);
			return {};
		}
	}, [isCelloEnabled]);

	const attachToForm = useCallback(
		(formElement: HTMLFormElement) => {
			if (!isCelloAvailable() || !window?.CelloAttribution) {
				return;
			}

			try {
				window?.CelloAttribution('attachTo', formElement);
			} catch (error) {
				console.error('Failed to attach to form:', error);
			}
		},
		[isCelloEnabled],
	);

	return {
		init,
		show,
		open,
		getAttributionData,
		attachToForm,
		attributionData,
		isCelloEnabled: isCelloAvailable(),
		isCelloHidden,
	};
};
